// ---------------------------------------------------------------------------
// useAdminThemes — os temas criados no painel, para quem precisa RESOLVER tema.
//
// Blueprint: `docs/systems/admin-themes.md`.
//
// ## Por que o cache é de MÓDULO, e não por componente
//
// Duas superfícies precisam desta lista e precisam da MESMA: o app do dono
// (`ThemeContext`) e o perfil público visto por outra pessoa
// (`usePublicProfileState`). Se cada uma buscasse por conta, elas poderiam
// chegar em respostas diferentes no mesmo instante — o dono vendo um tema que
// o visitante não vê, que é literalmente o BUG-80.
//
// Uma promessa compartilhada no módulo resolve isso por construção: quem chegar
// depois espera a mesma leitura, e todo mundo lê o mesmo resultado.
//
// ## Falha devolve lista VAZIA, nunca lança
//
// Sem a lista, um tema do painel cai em `default` no resolvedor. É falha
// fechada e, o que mais importa, **simétrica**: dono e visitante caem juntos.
// Um erro propagado aqui derrubaria o `ThemeProvider`, que embrulha o app.
// ---------------------------------------------------------------------------

import { useEffect, useState } from "react";
import { publishedAdminThemes, type ThemeConfig } from "../../config/themeConfigs";
import { fetchThemeConfigs } from "../../services/firebase/themeConfigService";

export interface AdminThemes {
  ids: string[];
  byId: Record<string, ThemeConfig>;
}

const VAZIO: AdminThemes = { ids: [], byId: {} };

/** Uma leitura por sessão, compartilhada por todos os consumidores. */
let pendente: Promise<AdminThemes> | null = null;

function carregar(): Promise<AdminThemes> {
  pendente ??= fetchThemeConfigs()
    .then((c) => publishedAdminThemes(c))
    .catch(() => VAZIO);
  return pendente;
}

/** Só para teste: descarta o cache entre casos. */
export function __resetAdminThemesCache(): void {
  pendente = null;
}

export function useAdminThemes(): AdminThemes {
  const [temas, setTemas] = useState<AdminThemes>(VAZIO);

  useEffect(() => {
    let vivo = true;
    void carregar().then((t) => {
      if (vivo) setTemas(t);
    });
    return () => {
      vivo = false;
    };
  }, []);

  return temas;
}
